import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import MediaCard from "@/components/feature/MediaCard";
import SiteLayout from "@/components/feature/SiteLayout";
import { useToast } from "@/components/feature/Toast";
import { useFavorites, useHistory } from "@/hooks/useLocalLibrary";
import { formatDate, getItemById, getRelated } from "@/hooks/useMediaQuery";
import { useSeo } from "@/hooks/useSeo";
import { typeLabels } from "@/mocks/filters";
import DetailHero from "@/pages/detail/components/DetailHero";
import EpisodeList from "@/pages/detail/components/EpisodeList";

export default function DetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const item = getItemById(id);
  const { isFavorite, toggle } = useFavorites();
  const { record } = useHistory();
  const { showToast } = useToast();

  useEffect(() => {
    if (item) record(item.id);
  }, [item, record]);

  useSeo(
    item ? `${item.title}详情 - 97视频` : "影视内容不存在或已下架 - 97视频",
    item
      ? `${item.title}（${item.year}）${typeLabels[item.type]}，由${item.director}执导，${item.cast
          .slice(0, 2)
          .join("、")}主演，${item.genres.join("、")}题材，${item.region}作品，最后更新于 ${item.updatedAt}。`
      : "该影视内容可能已下架或链接有误，可返回首页浏览其他电影、电视剧与综艺内容。",
    item ? `${item.title},${item.cast[0]},${item.genres.join(",")},${typeLabels[item.type]}` : "97视频,影视详情",
  );

  if (!item) {
    return (
      <SiteLayout>
        <div className="flex w-full flex-col items-center justify-center px-4 py-24 text-center">
          <i className="ri-film-line mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400" />
          <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            影视内容不存在或已下架
          </h1>
          <p className="mt-2 max-w-md text-[13px] leading-relaxed text-foreground-600">
            你访问的作品可能已经调整了资料或链接有误。可以回到首页，或从分类页面重新找到它。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-[13px] font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-home-4-line flex h-4 w-4 items-center justify-center" />
              返回首页
            </Link>
            <Link
              to="/category/movie"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-[13px] text-foreground-800 transition-colors hover:border-accent-300 hover:text-accent-600"
            >
              浏览电影频道
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelated(item, 6);
  const favorite = isFavorite(item.id);

  const handleToggle = () => {
    toggle(item.id);
    showToast(favorite ? "已取消收藏" : "已加入收藏，可在顶部收藏面板查看", "success");
  };

  return (
    <SiteLayout>
      <DetailHero item={item} isFavorite={favorite} onToggleFavorite={handleToggle} />

      <div className="w-full px-4 py-8 md:px-6 md:py-10">
        <nav className="mb-6 flex flex-wrap items-center gap-1.5 font-label text-[11px] text-foreground-500">
          <Link to="/" className="transition-colors hover:text-accent-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line flex h-3.5 w-3.5 items-center justify-center" />
          <Link to={`/category/${item.type}`} className="transition-colors hover:text-accent-600">
            {typeLabels[item.type]}
          </Link>
          <i className="ri-arrow-right-s-line flex h-3.5 w-3.5 items-center justify-center" />
          <span className="text-foreground-800">{item.title}</span>
        </nav>

        <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
          <div className="min-w-0 flex-1">
            <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950 md:text-base">
                <i className="ri-file-text-line flex h-4 w-4 items-center justify-center text-accent-600" />
                剧情简介
              </h2>
              <p className="mt-3 text-[13px] leading-[1.9] text-foreground-700">{item.synopsis}</p>
              <div className="mt-5 grid grid-cols-2 gap-3 border-t border-background-200 pt-4 sm:grid-cols-4">
                {[
                  { label: "评分", value: item.rating.toFixed(1) },
                  { label: "集数", value: item.episodes > 1 ? `${item.episodes} 集` : "单片" },
                  { label: "时长", value: item.duration },
                  { label: "更新", value: formatDate(item.updatedAt) },
                ].map((stat) => (
                  <div key={stat.label}>
                    <p className="font-label text-[10px] uppercase tracking-[0.14em] text-foreground-400">
                      {stat.label}
                    </p>
                    <p className="mt-1 font-heading text-[13px] font-semibold text-foreground-900">{stat.value}</p>
                  </div>
                ))}
              </div>
            </section>

            <div className="mt-5">
              <EpisodeList item={item} />
            </div>

            <section className="mt-5 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h2 className="font-heading text-sm font-bold text-foreground-950 md:text-base">所属分类</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link
                  to={`/category/${item.type}`}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-50 px-3 py-1.5 text-[11px] text-primary-700 transition-colors hover:bg-primary-100"
                >
                  <i className="ri-price-tag-3-line flex h-3.5 w-3.5 items-center justify-center" />
                  {typeLabels[item.type]}
                </Link>
                {item.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category/${item.type}`}
                    className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1.5 text-[11px] text-secondary-800 transition-colors hover:bg-secondary-200"
                  >
                    {genre}
                  </Link>
                ))}
                <span className="whitespace-nowrap rounded-full bg-accent-50 px-3 py-1.5 text-[11px] text-accent-800">
                  {item.region}
                </span>
              </div>
            </section>
          </div>

          <aside className="w-full shrink-0 lg:w-[300px]">
            <div className="sticky top-[88px] flex flex-col gap-4">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <h2 className="font-heading text-[13px] font-bold text-foreground-950">演职员</h2>
                <ul className="mt-3 flex flex-col gap-2.5">
                  <li className="flex items-center gap-2.5">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-[11px] text-primary-700">
                      导
                    </span>
                    <div>
                      <p className="text-[12px] text-foreground-900">{item.director}</p>
                      <p className="font-label text-[10px] text-foreground-400">导演</p>
                    </div>
                  </li>
                  {item.cast.map((person) => (
                    <li key={person} className="flex items-center gap-2.5">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-100 text-[11px] text-accent-800">
                        {person.slice(0, 1)}
                      </span>
                      <div>
                        <p className="text-[12px] text-foreground-900">{person}</p>
                        <p className="font-label text-[10px] text-foreground-400">主演</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <h2 className="font-heading text-[13px] font-bold text-foreground-950">分享</h2>
                <p className="mt-1 text-[11px] text-foreground-500">复制当前页面链接，分享给朋友</p>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(window.location.href);
                      showToast("链接已复制到剪贴板", "success");
                    } catch {
                      showToast("复制失败，请手动复制浏览器地址栏链接");
                    }
                  }}
                  className="mt-3 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-[12px] text-foreground-800 transition-colors hover:border-accent-300 hover:text-accent-600"
                >
                  <i className="ri-link flex h-4 w-4 items-center justify-center" />
                  复制页面链接
                </button>
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-10">
          <div className="mb-5 flex items-end justify-between">
            <div>
              <p className="mb-2 flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.22em] text-accent-600">
                <span className="h-px w-6 bg-accent-500" />
                You May Also Like
              </p>
              <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">相关推荐</h2>
              <p className="mt-1.5 text-[12px] text-foreground-500">基于同类型与标签为你关联的作品</p>
            </div>
            <Link
              to={`/category/${item.type}`}
              className="group flex items-center gap-1 whitespace-nowrap text-[12px] text-foreground-600 transition-colors hover:text-accent-600"
            >
              更多{typeLabels[item.type]}
              <i className="ri-arrow-right-line flex h-4 w-4 items-center justify-center group-hover:translate-x-0.5" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
            {related.map((rel) => (
              <MediaCard key={rel.id} item={rel} />
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}